import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Divider,
  Box,
  IconButton,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Stack,
} from "@mui/material";
import {
  CloseOutlined,
  SearchOutlined,
  CloudDownloadOutlined,
} from "@ant-design/icons";
import LoadingButton from "@mui/lab/LoadingButton";
import { IExportColumnField } from "common/models/IExportColumn";

export interface ExportExcelDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  columns: IExportColumnField[];
  onExport: (selectedFields: string[]) => void;
  loading?: boolean;
}
export const ExportExcelDialog: React.FC<ExportExcelDialogProps> = ({
  open,
  onClose,
  title = "Export to Excel - Select Columns",
  columns,
  onExport,
  loading = false,
}) => {
  // 1. Prepend virtual "Sr No" column at the top
  // 1. Check for existing Sr / SrNo column (prevent duplicate)
  const allColumns = useMemo(() => {
    const safeCols = columns || [];
    const isSrCol = (name: string) => {
      const lower = (name || "").toLowerCase().trim();
      return (
        lower === "sr" ||
        lower === "srno" ||
        lower === "sr no" ||
        lower === "sr.no" ||
        lower === "sr.no."
      );
    };

    const hasSr = safeCols.some((c) => isSrCol(c.fieldName));

    // If the procedure does NOT have an Sr column, prepend virtual "Sr No"
    if (!hasSr && safeCols.length > 0) {
      return [
        { fieldName: "SrNo", displayName: "Sr No", groupName: "" },
        ...safeCols,
      ];
    }

    // If the procedure ALREADY has an "Sr" column, unify its label to "Sr No" (no duplicate)
    return safeCols.map((c) =>
      isSrCol(c.fieldName) ? { ...c, displayName: "Sr No" } : c,
    );
  }, [columns]);

  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Select all columns (including SrNo) by default
  useEffect(() => {
    if (allColumns.length > 0) {
      setSelectedFields(allColumns.map((c) => c.fieldName));
    }
  }, [allColumns]);

  // Filter columns by user search
  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) return allColumns;
    const q = searchQuery.toLowerCase();
    return allColumns.filter(
      (c) =>
        c.displayName.toLowerCase().includes(q) ||
        c.fieldName.toLowerCase().includes(q),
    );
  }, [allColumns, searchQuery]);

  const isAllSelected =
    allColumns.length > 0 && selectedFields.length === allColumns.length;
  const isIndeterminate =
    selectedFields.length > 0 && selectedFields.length < allColumns.length;

  const handleToggle = (fieldName: string) => {
    setSelectedFields((prev) =>
      prev.includes(fieldName)
        ? prev.filter((f) => f !== fieldName)
        : [...prev, fieldName],
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedFields(checked ? allColumns.map((c) => c.fieldName) : []);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h5" color="primary">
              {title}
            </Typography>
            <Chip
              label={`${selectedFields.length} of ${allColumns.length} selected`}
              size="small"
              color={selectedFields.length > 0 ? "primary" : "default"}
              variant="outlined"
            />
          </Stack>
          <IconButton
            onClick={loading ? undefined : onClose}
            disabled={loading}
            size="small"
          >
            <CloseOutlined />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Select the columns you want to appear in the exported Excel
          spreadsheet:
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="Search columns..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined />
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 1,
            py: 0.5,
            bgcolor: "grey.100",
            borderRadius: 1,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            }
            label={<strong>Select All Columns</strong>}
          />
          {selectedFields.length < columns.length && (
            <Button
              size="small"
              onClick={() => setSelectedFields(columns.map((c) => c.fieldName))}
            >
              Reset to All
            </Button>
          )}
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box
          sx={{
            maxHeight: 340,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            px: 1,
          }}
        >
          {filteredColumns.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ py: 3 }}
            >
              No columns match &quot;{searchQuery}&quot;
            </Typography>
          ) : (
            filteredColumns.map((col) => (
              <FormControlLabel
                key={col.fieldName}
                control={
                  <Checkbox
                    checked={selectedFields.includes(col.fieldName)}
                    onChange={() => handleToggle(col.fieldName)}
                  />
                }
                label={col.displayName || col.fieldName}
                sx={{ py: 0.2 }}
              />
            ))
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}> 
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={() => onExport(selectedFields)}
          loading={loading}
          disabled={loading || selectedFields.length === 0}
          startIcon={<CloudDownloadOutlined />}
        >
          Download Excel
        </LoadingButton>
         <Button
          variant="contained"
          onClick={onClose}
          disabled={loading}
          color="secondary"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
